import React, { useState, useEffect, useCallback } from 'react'
import { fmtTime } from '../timeHelpers'
import { AVAILABILITY_RESTRICTIONS_ENABLED, getTeacherAvailability, getAvailableSlotsForDate, hasAvailabilityOnWeekday, DEFAULT_DURATION_MINUTES } from '../availabilityHelper'

// Self-contained month grid + slot list — deliberately NOT reusing
// ClassScheduleView.js's MiniCalendarView (which is unexported and used
// across Admin/Teacher/Student dashboards); a small standalone
// implementation here is lower risk than generalizing a heavily-used
// component just for this one picker.
export default function TeacherSlotPicker({ teacherId, durationMinutes = DEFAULT_DURATION_MINUTES, selectedDate, onPick }) {
  const today = new Date()
  const [viewYear,  setViewYear]  = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [availRows, setAvailRows] = useState([])
  const [slots,      setSlots]    = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)

  useEffect(() => {
    if (!teacherId) { setAvailRows([]); return }
    getTeacherAvailability(teacherId).then(setAvailRows)
  }, [teacherId])

  const loadSlots = useCallback(async () => {
    if (!teacherId || !selectedDate) { setSlots([]); return }
    setLoadingSlots(true)
    const s = await getAvailableSlotsForDate({ teacherId, date: selectedDate, durationMinutes })
    setSlots(s)
    setLoadingSlots(false)
  }, [teacherId, selectedDate, durationMinutes])

  useEffect(() => { loadSlots() }, [loadSlots])

  if (!teacherId || !AVAILABILITY_RESTRICTIONS_ENABLED) return null

  const todayStr = new Date().toISOString().slice(0, 10)
  const firstDay    = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  function pad(n) { return String(n).padStart(2, '0') }
  function dateStr(d) { return `${viewYear}-${pad(viewMonth + 1)}-${pad(d)}` }

  return (
    <div style={{ background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'12px', padding:'14px', marginTop:'8px', marginBottom:'8px' }}>
      <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a35))', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'10px' }}>
        🗓 Teacher Availability
      </div>

      {availRows.length === 0 ? (
        <div style={{ fontSize:'12px', color:'rgba(244,163,53,0.9)', background:'rgba(244,163,53,0.08)', padding:'8px 10px', borderRadius:'8px' }}>
          ⚠ No working hours set for this teacher yet — scheduling is unrestricted for now.
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
              <button type="button" onClick={() => { if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1)}else setViewMonth(m=>m-1) }}
                style={{ background:'rgba(var(--fg-rgb),0.06)', border:'none', color:'rgba(var(--fg-rgb),var(--a50))', borderRadius:'8px', width:'26px', height:'26px', cursor:'pointer', fontSize:'13px' }}>‹</button>
              <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>
                {new Date(viewYear, viewMonth).toLocaleString('en-IN', { month:'long', year:'numeric' })}
              </div>
              <button type="button" onClick={() => { if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1)}else setViewMonth(m=>m+1) }}
                style={{ background:'rgba(var(--fg-rgb),0.06)', border:'none', color:'rgba(var(--fg-rgb),var(--a50))', borderRadius:'8px', width:'26px', height:'26px', cursor:'pointer', fontSize:'13px' }}>›</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'2px', marginBottom:'4px' }}>
              {['S','M','T','W','T','F','S'].map((d,i) => (
                <div key={i} style={{ textAlign:'center', fontSize:'9px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a25))' }}>{d}</div>
              ))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'2px' }}>
              {cells.map((d, i) => {
                if (!d) return <div key={i}/>
                const ds       = dateStr(d)
                const isPast   = ds < todayStr
                const working  = hasAvailabilityOnWeekday(availRows, new Date(ds+'T00:00:00').getDay())
                const isSel    = ds === selectedDate
                return (
                  <button key={i} type="button" disabled={isPast || !working}
                    onClick={() => onPick?.({ date: ds, time: null })}
                    style={{
                      padding:'5px 2px', borderRadius:'6px', border:'none',
                      cursor: (isPast || !working) ? 'not-allowed' : 'pointer',
                      background: isSel ? '#1e90ff' : (working && !isPast) ? 'rgba(16,185,129,0.12)' : 'transparent',
                      color: isSel ? '#fff' : isPast ? 'rgba(var(--fg-rgb),0.15)' : working ? '#34d399' : 'rgba(var(--fg-rgb),var(--a20))',
                      fontSize:'11px', fontWeight: working ? 700 : 400,
                    }}>{d}</button>
                )
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'8px', textTransform:'uppercase' }}>
              {selectedDate ? new Date(selectedDate+'T00:00:00').toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'}) : 'Pick a date'}
            </div>
            {!selectedDate ? (
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>Select a green day on the left.</div>
            ) : loadingSlots ? (
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>Loading slots...</div>
            ) : slots.length === 0 ? (
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>No working hours on this day.</div>
            ) : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', maxHeight:'160px', overflowY:'auto' }}>
                {slots.map(s => (
                  <button key={s.time} type="button" disabled={s.status === 'booked'}
                    onClick={() => onPick?.({ date: selectedDate, time: s.time })}
                    title={s.status === 'booked' ? 'Already booked' : 'Available'}
                    style={{
                      padding:'5px 10px', borderRadius:'7px', border:'none', cursor: s.status==='booked' ? 'not-allowed' : 'pointer',
                      fontSize:'11px', fontWeight:700, fontFamily:'inherit',
                      background: s.status === 'booked' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.15)',
                      color: s.status === 'booked' ? 'rgba(248,113,113,0.6)' : '#34d399',
                      textDecoration: s.status === 'booked' ? 'line-through' : 'none',
                    }}>
                    {fmtTime(s.time)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
